import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, Clock, History } from 'lucide-react';
import { format } from 'date-fns';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import type { AlertData } from './AlertCard';

interface AlertsResponse {
  data: {
    alerts: AlertData[];
    total: number;
    has_more: boolean;
  };
}

interface AlertHistoryProps {
  farmId: string;
}

function formatAlertType(type: string): string {
  const labels: Record<string, string> = {
    low_do: 'Low DO',
    high_do: 'High DO',
    high_ph: 'High pH',
    low_ph: 'Low pH',
    high_ammonia: 'High Ammonia',
    high_nitrite: 'High Nitrite',
    ph_dial_high: 'pH Swing High',
    low_temp: 'Low Temp',
    high_temp: 'High Temp',
    low_salinity: 'Low Salinity',
    high_salinity: 'High Salinity',
  };
  return labels[type] || type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function AlertHistory({ farmId }: AlertHistoryProps) {
  const [dateRange, setDateRange] = useState('7d');
  const [search, setSearch] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['alerts', farmId, 'history', dateRange],
    queryFn: () => api.get<AlertsResponse>(`/api/v1/farms/${farmId}/alerts`, {
      params: { status: 'acknowledged', range: dateRange }
    }),
  });

  const alerts = data?.data?.alerts || [];

  // Filter by search
  const filteredAlerts = alerts.filter((alert) =>
    (alert.pond_name?.toLowerCase() || '').includes(search.toLowerCase()) ||
    alert.message.toLowerCase().includes(search.toLowerCase()) ||
    formatAlertType(alert.alert_type).toLowerCase().includes(search.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <LoadingSpinner size={32} />
        <p className="text-sm text-gray-500">Loading history...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search alerts..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="w-full sm:w-44">
          <Select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            options={[
              { value: '7d', label: 'Last 7 Days' },
              { value: '30d', label: 'Last 30 Days' },
              { value: '90d', label: 'Last 90 Days' },
            ]}
          />
        </div>
      </div>

      {/* History Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50/50">
                <th className="text-left py-3 px-4 font-medium text-gray-600">Pond</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">Alert</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">Severity</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" />
                    Triggered
                  </span>
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">Acknowledged</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredAlerts.map((alert) => (
                <tr
                  key={alert.id}
                  className="hover:bg-gray-50/50 transition-colors"
                >
                  <td className="py-3 px-4">
                    <span className="font-medium text-gray-900">
                      {alert.pond_name || 'Unknown'}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div>
                      <span className="text-gray-700">{formatAlertType(alert.alert_type)}</span>
                      <p className="text-xs text-gray-500 mt-0.5 max-w-xs truncate">
                        {alert.message}
                      </p>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <SeverityBadge severity={alert.severity} />
                  </td>
                  <td className="py-3 px-4 text-gray-500 whitespace-nowrap">
                    {format(new Date(alert.triggered_at), 'MMM d, h:mm a')}
                  </td>
                  <td className="py-3 px-4 text-gray-500 whitespace-nowrap">
                    {alert.acknowledged_at
                      ? format(new Date(alert.acknowledged_at), 'MMM d, h:mm a')
                      : '—'}
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-gray-400 max-w-[200px] truncate block">
                      {alert.acknowledgement_notes || '—'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredAlerts.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
              <History className="w-6 h-6 text-gray-400" />
            </div>
            <p className="text-gray-500">
              {search ? 'No alerts match your search' : 'No alert history found'}
            </p>
          </div>
        )}
      </Card>
    </div>
  );
}

function SeverityBadge({ severity }: { severity: 'critical' | 'warning' | 'info' }) {
  const config = {
    critical: 'bg-red-100 text-red-700',
    warning: 'bg-amber-100 text-amber-700',
    info: 'bg-blue-100 text-blue-700',
  }[severity];

  return (
    <span
      className={cn(
        'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize',
        config
      )}
    >
      {severity}
    </span>
  );
}
